import { Icon } from "../components/Icon.js";
import { useColoring } from "./ColoringContext.js";
import { SectionCard } from "./fields.js";
import { useProviderStatus } from "./providers.js";
import type { Consistency, ImageCapabilities } from "./types.js";

const LEVELS: { id: Consistency; label: string; desc: string }[] = [
  { id: "flexible", label: "Flexible", desc: "Prompt only. Most variety between pages." },
  { id: "balanced", label: "Balanced", desc: "Also sends each character's reference sheet." },
  { id: "strict", label: "Strict", desc: "Adds an earlier page as a style anchor and locks one seed for the whole book (where supported)." }
];

function Cap({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`cap ${ok ? "yes" : "no"}`}>
      <Icon name={ok ? "check" : "x"} size={11} strokeWidth={2.5} /> {label}
    </span>
  );
}

/** What can actually be promised for consistency with this provider, stated honestly. */
function consistencyNote(level: Consistency, caps: ImageCapabilities): string {
  const tools: string[] = [];
  if (caps.referenceImages && level !== "flexible") tools.push("character reference sheets");
  if (caps.referenceImages && level === "strict") tools.push("an earlier page as a style anchor");
  if (caps.seed && level === "strict") tools.push("a locked seed");
  tools.push("your Style Bible and character descriptions in every prompt");
  const lacks: string[] = [];
  if (!caps.referenceImages && level !== "flexible") lacks.push("it can't take reference images");
  if (!caps.seed && level === "strict") lacks.push("it has no seed control");
  return (
    `With this provider, ${level} mode uses ${tools.join(", ")}.` +
    (lacks.length ? ` Limits: ${lacks.join("; ")}.` : "") +
    " Image models don't guarantee identical characters, so review pages and use the quality check."
  );
}

/** Image provider choice, capabilities and Consistency Strength. */
export function GenerationSettings() {
  const { state, saveBook } = useColoring();
  const { book } = state;
  const status = useProviderStatus();
  const active = status?.active ?? null;
  const connected = status?.providers.filter((p) => p.configured) ?? [];

  return (
    <SectionCard className="span-2" icon="image" title="Image generation">
      <div className="coloring-fields">
        <div className="field">
          <label className="label">Image provider</label>
          <select
            className="select"
            value={book.imageProvider ?? ""}
            onChange={(e) => void saveBook({ imageProvider: e.target.value || null })}
            disabled={connected.length === 0}
          >
            <option value="">Automatic{active ? ` (${active.label})` : ""}</option>
            {connected.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
          {status && connected.length === 0 && (
            <div className="gen-note warn" style={{ marginTop: 8 }}>
              <Icon name="alert" size={14} />
              <span>
                No image provider is connected. Add one on the server to generate artwork:
                <ul className="hint-list">
                  {status.providers.filter((p) => p.id !== "mock").map((p) => (
                    <li key={p.id}><strong>{p.label}</strong> — {p.hint}</li>
                  ))}
                </ul>
              </span>
            </div>
          )}
        </div>

        <div className="field">
          <label className="label">Consistency strength</label>
          <div className="choice-row">
            {LEVELS.map((l) => (
              <button key={l.id} type="button" className={`choice ${book.consistency === l.id ? "on" : ""}`} onClick={() => void saveBook({ consistency: l.id })} title={l.desc}>
                {l.label}
              </button>
            ))}
          </div>
          <div className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>{LEVELS.find((l) => l.id === book.consistency)?.desc}</div>
        </div>
      </div>

      {active && (
        <>
          <div className="cap-row">
            <Cap ok={active.capabilities.referenceImages} label="Reference images" />
            <Cap ok={active.capabilities.imageEdit} label="Image editing" />
            <Cap ok={active.capabilities.imageToImage} label="Image-to-image" />
            <Cap ok={active.capabilities.seed} label="Seed" />
            <Cap ok={active.capabilities.negativePrompt} label="Negative prompt" />
          </div>
          <p className="faint" style={{ fontSize: 12, margin: "10px 0 0" }}>{consistencyNote(book.consistency, active.capabilities)}</p>
        </>
      )}
    </SectionCard>
  );
}
